import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, InputGroup, Intent, Position } from '@blueprintjs/core';
import Flex from '@components/Flex';
import DevText from '@components/Text';
import PlatformFilterButton from '@components/PlatformFilterButton';
import PlatformListFilter from '@components/PlatformListFilter';
import { ToastUtils } from '@app/data/utils';
import { ClientUserListItemResource } from 'dy-frontend-http-repository/lib/modules/ClientUser/resources';
import { fetchTaskKanbanFilterClients } from '../../store/effects';
import {
    ClientCompanyOption,
    filterCompanyOptionsByQuery,
    getSelectedCompanyLabel,
    groupClientsByCompany,
    isCompanyOptionChecked,
    isCompanyOptionIndeterminate,
    selectCompanyOptionsWithPaidSubscription,
    toggleCompanyOptionSelection,
} from './groupClientsByCompany';

export interface TaskKanbanClientsFilterProps {
    disabled?: boolean;
    selectedClientIds: ID[];
    onChange: (clientIds: ID[]) => void;
}

export type Props = TaskKanbanClientsFilterProps;

// Clients are compared by ID, so swapping one client for another one is applied as well
const getClientIdsKey = (clientIds: ID[]) => [...clientIds].sort().join(',');

const TaskKanbanClientsFilter: React.FC<Props> = ({ disabled = false, selectedClientIds, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [areClientsRequested, setAreClientsRequested] = useState(false);
    const [areClientsInitialized, setAreClientsInitialized] = useState(false);
    const [clients, setClients] = useState<ClientUserListItemResource[]>([]);
    const [checkedClientIds, setCheckedClientIds] = useState<ID[]>(selectedClientIds);
    const [search, setSearch] = useState('');

    // Clients are picked by business, so the inventory is collapsed into one option per unique company,
    // and only businesses which currently pay for a subscription are offered
    const companyOptions = useMemo(
        () => selectCompanyOptionsWithPaidSubscription(groupClientsByCompany(clients), clients),
        [clients]
    );

    // Searching narrows what is shown only, the complete inventory stays the source of selection and labels
    const visibleCompanyOptions = useMemo(
        () => filterCompanyOptionsByQuery(companyOptions, search),
        [companyOptions, search]
    );

    // Inventory spans several pages, so it is loaded once, when the popup is opened for the first time
    useEffect(() => {
        if (!isOpen || areClientsRequested) {
            return;
        }

        setAreClientsRequested(true);

        fetchTaskKanbanFilterClients()
            .then((foundClients) => setClients(foundClients))
            .catch((e) => {
                // Log
                console.error(e);

                ToastUtils.showToast({
                    message: 'Clients were NOT loaded, client filter is unavailable',
                    intent: Intent.DANGER,
                });
            })
            .finally(() => setAreClientsInitialized(true));
    }, [isOpen, areClientsRequested]);

    // Selection is owned by the board, so clearing or hydrating it outside of this popup is mirrored back
    useEffect(() => {
        setCheckedClientIds(selectedClientIds);
    }, [selectedClientIds]);

    const getFilterTagTitle = () => {
        return checkedClientIds.length > 0 ? 'Clients:' : 'Clients';
    };

    const getFilterTagLabel = () => {
        return getSelectedCompanyLabel(companyOptions, checkedClientIds);
    };

    const handleCheckboxClick = (option: ClientCompanyOption) => {
        setCheckedClientIds(toggleCompanyOptionSelection(option, checkedClientIds));
    };

    const handleApplyFilter = () => {
        if (getClientIdsKey(checkedClientIds) !== getClientIdsKey(selectedClientIds)) {
            // Checked clients changed since opening the filter
            onChange(checkedClientIds);
        }
    };

    const handleRemoveFilter = () => {
        setCheckedClientIds([]);
        setSearch('');
        setIsOpen(false);

        if (selectedClientIds.length > 0) {
            // Clearing clients restores the selected base scope instead of emptying the board
            onChange([]);
        }
    };

    const renderSearch = () => {
        if (!areClientsInitialized) {
            // Inventory is still loading, so there is nothing to search through yet
            return null;
        }

        // Businesses are matched by their shown label only, so no private client field is searchable
        const hasNoMatches = search.trim().length > 0 && visibleCompanyOptions.length === 0;

        return (
            <div className="mb-2">
                <InputGroup
                    leftIcon="search"
                    placeholder="Search client businesses"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />

                {hasNoMatches && (
                    <DevText className="mt-2" muted>
                        No businesses found
                    </DevText>
                )}
            </div>
        );
    };

    const renderRow = (option: ClientCompanyOption) => {
        const isChecked = isCompanyOptionChecked(option, checkedClientIds);

        return (
            <li className="mb-1" key={option.key}>
                <Flex justify="space-between" align="center">
                    <Checkbox
                        checked={isChecked}
                        indeterminate={!isChecked && isCompanyOptionIndeterminate(option, checkedClientIds)}
                        className="mr-2"
                        labelElement={<DevText inline>{option.label}</DevText>}
                        onClick={() => handleCheckboxClick(option)}
                    />
                </Flex>
            </li>
        );
    };

    return (
        <PlatformListFilter<ClientCompanyOption>
            isOpen={isOpen}
            loading={!areClientsInitialized}
            removable={checkedClientIds.length > 0}
            title="Clients"
            position={Position.BOTTOM_LEFT}
            minWidth={360}
            maxListHeight={400}
            headerContent={renderSearch()}
            list={visibleCompanyOptions}
            itemRenderer={renderRow}
            onApply={handleApplyFilter}
            onClose={() => {
                setIsOpen(false);
                setSearch('');
            }}
            onRemove={handleRemoveFilter}
        >
            <PlatformFilterButton
                icon="person"
                disabled={disabled}
                onClick={() => setIsOpen(!isOpen)}
                label={getFilterTagLabel()}
                title={getFilterTagTitle()}
            />
        </PlatformListFilter>
    );
};

export default TaskKanbanClientsFilter;
